import { useEffect, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

// ── ManageArApiModal — opened from the 🔌 API button in AR Master. Lets an
// admin generate/rotate/revoke the API key an external system (e.g. a
// Power Automate flow) uses to POST AR record JSON straight into this
// table, without ever opening a browser session — see
// kosmos_app/ar_views/webhook.py and api_credential.py on the backend.
// Mirrors Legal_Elements/ManageLegalApiModal.js exactly; the only
// differences are the webhook path/fields (AR has three tables — Aging,
// Payment History, Renewal Pending — keyed in one request body, instead
// of Legal's single flat list). The raw key is only ever shown once, right
// after Generate/Rotate — refreshing or reopening this modal only shows
// metadata (label, created by/when, last used), never the key itself,
// since only its hash is stored server-side.

const WEBHOOK_PATH = '/api/ar/webhook/';

// Every field of every table shown filled in — not just id + one changed
// field — so a caller can see the complete shape of each row, same as
// what "How it works" lists out in words.
const EXAMPLE_PAYLOAD = JSON.stringify({
    aging: [
        { customer: 'Acme Corp', end_user: 'Acme Corp', region: 'North America', sales_rep: 'Jane Doe', type: 'Invoice', date: '2026-01-15', doc: 'INV-1021', due: '2026-02-15', bal: 12500 },
    ],
    payments: [
        { invoice_number: 'INV-1021', customer: 'Acme Corp', end_user: 'Acme Corp', sales_rep: 'Jane Doe', region: 'North America', payment_type: 'Wire', date: '2026-01-20', due_date: '2026-02-15', amount: 12500 },
    ],
    renewals: [
        { end_user: 'Acme Corp', renewal_status: '12 Months', status: 'Pending', amount: 20000, sales_rep: 'Jane Doe', region: 'North America', remarks: 'Renewal in Q2' },
    ],
}, null, 2);

function CopyButton({ value, label = 'Copy' }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard access can fail (permissions, non-secure context) —
            // the value is still selectable/visible in the field next to
            // this button, so there's a manual fallback either way.
        }
    };
    return (
        <button type="button" className="legal-btn-cancel legal-cat-inline-btn" onClick={copy}>
            {copied ? 'Copied ✓' : label}
        </button>
    );
}

function ManageArApiModal({ canManageData = false, onClose }) {
    const [credential, setCredential] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [newKey, setNewKey] = useState(null);
    const [label, setLabel] = useState('');

    const load = async () => {
        setLoading(true); setError('');
        try {
            const res = await fetch(`${API_URL}/api/ar/api-credential/`, { credentials: 'include' });
            const body = await readApiJson(res, 'Could not load the API key status.');
            setCredential(body.credential || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const postAction = async (action) => {
        setBusy(true); setError('');
        try {
            const res = await fetch(`${API_URL}/api/ar/api-credential/`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ action, label: label.trim() }),
            });
            const body = await readApiJson(res, 'Request failed.');
            setCredential(body.credential || null);
            setNewKey(body.api_key || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const rotate = () => {
        if (credential && !window.confirm('This replaces the current key — anything still using the old one will stop working immediately. Continue?')) return;
        postAction('rotate');
    };

    const revoke = () => {
        if (!window.confirm('Revoke the API key? Any integration using it will stop working until a new key is generated.')) return;
        postAction('revoke').then(() => setNewKey(null));
    };

    if (!canManageData) return null;

    const webhookUrl = `${API_URL}${WEBHOOK_PATH}`;

    return (
        // The `.legal-*` classes this modal borrows (legal-modal, legal-btn-save,
        // legal-search, ...) read their colors from --lg-* custom properties
        // that LegalMain.css only declares on `.legal-dashboard` — the root
        // wrapper of the Legal page itself. Mounted here, inside the AR
        // Dashboard's tree, none of those variables resolve, so e.g.
        // `background: var(--lg-pu)` falls through to transparent and
        // `color: #fff` renders invisible white-on-white (same bug class as
        // the earlier --bl/.bm-export-btn hover fix). Redeclaring the same
        // token values here — not applying the `.legal-dashboard` class
        // itself, which also carries unrelated page-level layout rules —
        // makes every borrowed `.legal-*` class render correctly regardless
        // of which dashboard mounts it.
        <div
            className="legal-modal-bg"
            style={{
                '--lg-nv': '#0f172a', '--lg-pu': '#7c3aed', '--lg-pl': '#ede9fe', '--lg-mu': '#65676b',
                '--lg-bd': '#dddfe2', '--lg-gn': '#15803d', '--lg-gb': '#dcfce7', '--lg-am': '#b45309',
                '--lg-ab': '#fef3c7', '--lg-rd': '#b91c1c', '--lg-rb': '#fee2e2',
            }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            {/* Wider than .legal-modal-wide (760px) and laid out as two
                columns — the old single-column stack pushed "How it works"
                and the example payload below the fold, needing a scroll
                just to see they existed. Scoped with an inline style
                (not a shared class) so this doesn't widen Legal's own copy
                of this modal. */}
            <div className="legal-modal legal-modal-wide" style={{ maxWidth: 1100 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🔌</div>
                    <div>
                        <div className="legal-modal-title">API Access</div>
                        <div className="legal-modal-sub">Let an external system (e.g. Power Automate) push AR records in as JSON</div>
                    </div>
                    <button className="legal-modal-close" onClick={onClose} style={{ marginLeft: 'auto' }}>✕</button>
                </div>
                <div className="legal-modal-body" style={{ maxHeight: '78vh' }}>
                    {error && <div className="legal-form-error">{error}</div>}

                    {loading ? <p style={{ color: '#65676b' }}>Loading…</p> : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
                            {/* Left column — the key itself and the controls to manage it */}
                            <div>
                                <div className="legal-field" style={{ marginBottom: 12 }}>
                                    <label>Webhook URL</label>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <input className="legal-cat-inline-input" style={{ flex: 1 }} readOnly value={webhookUrl} onFocus={e => e.target.select()} />
                                        <CopyButton value={webhookUrl} />
                                    </div>
                                </div>

                                {newKey && (
                                    <div className="legal-field" style={{ marginBottom: 12 }}>
                                        <label>New API key — copy it now, it won't be shown again</label>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <input className="legal-cat-inline-input" style={{ flex: 1, fontFamily: 'monospace' }} readOnly value={newKey} onFocus={e => e.target.select()} />
                                            <CopyButton value={newKey} />
                                        </div>
                                    </div>
                                )}

                                {credential ? (
                                    <div className="legal-field" style={{ marginBottom: 12 }}>
                                        <label>Current key</label>
                                        <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                                            <strong>{credential.label}</strong> — created by {credential.created_by || 'unknown'}
                                            {credential.create_date && ` on ${new Date(credential.create_date).toLocaleString()}`}
                                            <br />
                                            Last used: {credential.last_used_date ? new Date(credential.last_used_date).toLocaleString() : 'never'}
                                        </div>
                                    </div>
                                ) : (
                                    <p style={{ fontSize: 12.5, color: '#65676b', marginBottom: 12 }}>No API key yet — generate one below to enable the webhook.</p>
                                )}

                                {/* Stacked (input above buttons) rather than one flex row —
                                    a single row let the narrower two-column layout squeeze
                                    the buttons out of view behind the search input's
                                    unshrinkable intrinsic width. This way each piece always
                                    gets its own full-width line, regardless of column width. */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <input
                                        className="legal-search legal-cat-search"
                                        style={{ width: '100%' }}
                                        placeholder="Label (e.g. Power Automate — Finance)"
                                        value={label}
                                        onChange={e => setLabel(e.target.value)}
                                    />
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button type="button" className="legal-btn-save legal-cat-add-btn" disabled={busy} onClick={rotate}>
                                            {busy ? '…' : credential ? 'Rotate Key' : 'Generate Key'}
                                        </button>
                                        {credential && (
                                            <button type="button" className="legal-btn-cancel legal-cat-inline-btn" disabled={busy} onClick={revoke}>
                                                Revoke
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right column — reference material, nothing to interact with.
                                Example payload used to live here too, but a half-width
                                column is cramped for indented JSON and it also left the
                                shorter left column (which runs out of content after the
                                buttons) with a large empty gap below it. Moving it to its
                                own full-width row underneath fixes both at once. */}
                            <div>
                                <div className="legal-field">
                                    <label>How it works</label>
                                    <ul style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, paddingLeft: 16, margin: 0 }}>
                                        <li>POST JSON to the webhook URL with header <code>X-Api-Key: &lt;your key&gt;</code>.</li>
                                        <li>Body is one object keyed by table: <code>aging</code>, <code>payments</code>, <code>renewals</code> — include whichever you're updating, each an array of records.</li>
                                        <li>Omit <code>id</code> on a row to create a new record; include the <code>id</code> from a prior response (or from AR Master) to update it.</li>
                                        <li>AR Aging: customer, end_user, region, sales_rep, type, date (YYYY-MM-DD), doc, due (YYYY-MM-DD), bal.</li>
                                        <li>Payment History: invoice_number, customer, end_user, sales_rep, region, payment_type, date, due_date, amount.</li>
                                        <li>Renewal Pending: end_user, renewal_status, status, amount, sales_rep, region, remarks.</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="legal-field" style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                                <label>Example payload</label>
                                <pre style={{ fontSize: 11.5, background: '#f8fafd', border: '1px solid #dddfe2', borderRadius: 8, padding: 14, overflow: 'auto', maxHeight: 280, margin: 0, lineHeight: 1.55 }}>{EXAMPLE_PAYLOAD}</pre>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export { ManageArApiModal };
export default ManageArApiModal;
